import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NiceModal from '@ebay/nice-modal-react';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { DailySummaryMap, LabDashboardEventSummary, LabStatusCountMap } from '@/src/types/LabDashboard';
import { formatDateKey, getCalendarGrid, isInMonth } from './calendar.utils';
import { getOngoingEvents, buildTableRows } from './dailySummaryModal.utils';
import DayCell from './DayCell';
import { DayCellData } from './DayCell';
import DailySummaryModal from './DailySummaryModal';
import styles from './LabCalendar.module.scss';

export interface CalendarGridProps {
  displayedMonth: Date;
  renderDayContent?: (date: Date, isCurrentMonth: boolean) => React.ReactNode | null;
  dailySummary?: DailySummaryMap;
  calendarEvents?: LabDashboardEventSummary[];
}

/**
 * Day-of-week i18n keys in column order (Sunday through Saturday).
 */
const DAY_KEYS = [
  i18nKeys.labDashboard.calendar.daysOfWeek.sun,
  i18nKeys.labDashboard.calendar.daysOfWeek.mon,
  i18nKeys.labDashboard.calendar.daysOfWeek.tue,
  i18nKeys.labDashboard.calendar.daysOfWeek.wed,
  i18nKeys.labDashboard.calendar.daysOfWeek.thu,
  i18nKeys.labDashboard.calendar.daysOfWeek.fri,
  i18nKeys.labDashboard.calendar.daysOfWeek.sat,
];

/**
 * Returns true if the date is today or in the past (not in the future).
 * Accepts a pre-computed `today` with hours zeroed to avoid redundant Date allocations.
 */
const isDateEligible = (date: Date, today: Date): boolean => {
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return target <= today;
};

/**
 * A campaign event pre-indexed for active-window lookups. `challengeCount` is
 * the nominal 1-lab-per-challenge contribution; `startKey`/`endKey` are the
 * inclusive active window as 'yyyy-MM-dd' date strings (local timezone).
 * `endKey` is null when the campaign has no end date (active indefinitely).
 */
export interface CampaignEventWindow {
  challengeCount: number;
  startKey: string;
  endKey: string | null;
}

/**
 * Calculates the predicted lab usage for a given date.
 *
 * Provisioning differs by event type:
 *
 * Non-campaign events are provisioned from participant capacity and contribute
 * ceil(maxParticipants / teamSize) * challengeCount labs. They are counted only
 * on their start day (a one-day spike), so they are looked up in the
 * start-day-indexed `eventsByDate` map.
 *
 * Campaign events keep 1 lab per challenge provisioned and ready at all times so
 * a user can start a lab immediately on joining. Because that provisioning is
 * maintained for the whole active window, each campaign event contributes
 * `challengeCount` labs to every day within its [startKey, endKey] window
 * (inclusive), not just its start day. They are matched from `campaignWindows`.
 *
 * Events are pre-filtered during map construction (null fields excluded per type),
 * so non-null assertions are safe in the hot path.
 *
 * @param eventsByDate - Non-campaign events indexed by their start-day 'yyyy-MM-dd' key
 * @param campaignWindows - Campaign events with their inclusive active-window keys
 * @param dateKey - Date string in 'yyyy-MM-dd' format (output of formatDateKey)
 * @returns The total predicted lab count (0 if no eligible events match)
 */
export const calculatePredictedLabUsage = (
  eventsByDate: Map<string, LabDashboardEventSummary[]>,
  campaignWindows: CampaignEventWindow[],
  dateKey: string
): number => {
  let total = 0;

  // Non-campaign events: capacity-based, counted on their start day only.
  const events = eventsByDate.get(dateKey);
  if (events) {
    for (const event of events) {
      // Safe: events are pre-filtered in the useMemo to exclude null values.
      const teams = Math.ceil((event.maxParticipants as number) / (event.teamSize as number));
      total += teams * (event.challengeCount as number);
    }
  }

  // Campaign events: 1 lab per challenge maintained across the whole active
  // window. Keys are 'yyyy-MM-dd', so lexicographic comparison is date-correct.
  for (const campaign of campaignWindows) {
    if (dateKey < campaign.startKey) continue;
    if (campaign.endKey != null && dateKey > campaign.endKey) continue;
    total += campaign.challengeCount;
  }

  return total;
};

/**
 * Computes the 2D date grid for the displayed month and renders
 * day-of-week column headers and rows of DayCell components.
 * Implements roving tabindex keyboard navigation pattern.
 */
const CalendarGrid: React.FC<CalendarGridProps> = ({
  displayedMonth,
  renderDayContent,
  dailySummary,
  calendarEvents,
}) => {
  const { t } = useTranslation();
  const normalizedToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const today = normalizedToday;
  const year = displayedMonth.getFullYear();
  const month = displayedMonth.getMonth();
  const grid = useMemo(() => getCalendarGrid(year, month), [year, month]);

  // Non-campaign events indexed by their start-day key (capacity-based, one-day spike).
  const eventsByDate = useMemo(() => {
    const map = new Map<string, LabDashboardEventSummary[]>();
    for (const event of calendarEvents ?? []) {
      if (event.campaignEvent === true) continue;
      // challengeCount and startDate are required, plus capacity fields.
      if (event.challengeCount == null) continue;
      if (event.startDate == null) continue;
      if (event.maxParticipants == null) continue;
      if (event.teamSize == null || event.teamSize === 0) continue;

      // Parse the ISO date string to get the local-timezone date for correct calendar placement
      const dateKey = formatDateKey(new Date(event.startDate));
      const existing = map.get(dateKey);
      if (existing) {
        existing.push(event);
      } else {
        map.set(dateKey, [event]);
      }
    }
    return map;
  }, [calendarEvents]);

  // Campaign events keep 1 lab per challenge provisioned across their whole active
  // window, so they are matched by [startKey, endKey] range rather than start day.
  const campaignWindows = useMemo(() => {
    const windows: CampaignEventWindow[] = [];
    for (const event of calendarEvents ?? []) {
      if (event.campaignEvent !== true) continue;
      if (event.challengeCount == null) continue;
      if (event.startDate == null) continue;

      windows.push({
        challengeCount: event.challengeCount,
        startKey: formatDateKey(new Date(event.startDate)),
        // A null endDate means the campaign is active indefinitely.
        endKey: event.endDate == null ? null : formatDateKey(new Date(event.endDate)),
      });
    }
    return windows;
  }, [calendarEvents]);

  // 2D ref grid for direct cell access (replaces querySelector)
  const cellRefs = useRef<(HTMLTableCellElement | null)[][]>(
    Array.from({ length: 6 }, () => Array<HTMLTableCellElement | null>(7).fill(null))
  );

  // Track current focused position in a ref so handleDayClick can read it without re-creating
  const focusedPositionRef = useRef({ row: 0, col: 0 });

  const handleDayClick = useCallback(
    (date: Date) => {
      // Record the triggering cell coordinates for focus restoration
      const triggerCell = { row: focusedPositionRef.current.row, col: focusedPositionRef.current.col };
      const events = getOngoingEvents(date, calendarEvents ?? []);
      const rows = buildTableRows(events);

      NiceModal.show(DailySummaryModal, { selectedDate: date, events: rows }).then(() => {
        // Restore focus to the triggering cell after modal closes
        cellRefs.current[triggerCell.row]?.[triggerCell.col]?.focus();
      });
    },
    [calendarEvents]
  );

  // Roving tabindex: track which cell is the active (focusable) cell
  const [focusedRow, setFocusedRow] = useState(0);
  const [focusedCol, setFocusedCol] = useState(0);

  // Keep the ref in sync with state for stable callback access
  useEffect(() => {
    focusedPositionRef.current = { row: focusedRow, col: focusedCol };
  }, [focusedRow, focusedCol]);

  // Pre-compute which cells are in the current month for navigation skipping
  const inMonthGrid = useMemo(
    () => grid.map((week) => week.map((date) => isInMonth(date, year, month))),
    [grid, year, month]
  );

  // Reset focus to first in-month cell when month changes to prevent stale coordinates.
  // Depend on primitive year/month values rather than the Date object reference
  // so that re-renders with a new Date instance for the same month don't reset focus.
  useEffect(() => {
    // Find the first in-month cell by computing which day of the week the month starts on
    const firstDayOfWeek = new Date(year, month, 1).getDay(); // 0=Sunday
    setFocusedRow(0);
    setFocusedCol(firstDayOfWeek);
  }, [year, month]);

  // Pre-compute per-cell data for the entire grid
  const cellDataGrid = useMemo(() => {
    return grid.map((week) =>
      week.map((date): DayCellData => {
        const inMonth = isInMonth(date, year, month);
        const externalContent = renderDayContent?.(date, inMonth) ?? undefined;

        if (!inMonth) {
          return { externalContent };
        }

        // Future in-month dates: show predicted usage
        if (!isDateEligible(date, normalizedToday)) {
          const dateKey = formatDateKey(date);
          const predictedLabs = calculatePredictedLabUsage(eventsByDate, campaignWindows, dateKey);
          return { externalContent, predictedLabs: predictedLabs > 0 ? predictedLabs : undefined };
        }

        // Past/today in-month dates: show daily summary
        const dateKey = formatDateKey(date);
        const statusMap: LabStatusCountMap | undefined = dailySummary?.[dateKey];
        return { externalContent, dailySummary: statusMap };
      })
    );
  }, [grid, year, month, renderDayContent, normalizedToday, eventsByDate, campaignWindows, dailySummary]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      let newRow = focusedRow;
      let newCol = focusedCol;

      switch (event.key) {
        case 'ArrowUp':
          newRow = Math.max(0, focusedRow - 1);
          event.preventDefault();
          break;
        case 'ArrowDown':
          newRow = Math.min(grid.length - 1, focusedRow + 1);
          event.preventDefault();
          break;
        case 'ArrowLeft':
          newCol = Math.max(0, focusedCol - 1);
          event.preventDefault();
          break;
        case 'ArrowRight':
          newCol = Math.min(6, focusedCol + 1);
          event.preventDefault();
          break;
        default:
          return;
      }

      // Skip out-of-month cells: if target is blank, stay in place
      if (!inMonthGrid[newRow]?.[newCol]) {
        return;
      }

      setFocusedRow(newRow);
      setFocusedCol(newCol);

      // Move DOM focus to the new cell via ref
      cellRefs.current[newRow]?.[newCol]?.focus();
    },
    [focusedRow, focusedCol, grid.length, inMonthGrid]
  );

  return (
    <table role="grid" onKeyDown={handleKeyDown} className={styles.grid}>
      <thead>
        <tr role="row">
          {DAY_KEYS.map((key, index) => (
            <th key={index} role="columnheader" scope="col" className={styles.columnHeader}>
              {t(key)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {grid.map((week, rowIndex) => (
          <tr key={rowIndex} role="row">
            {week.map((date, colIndex) => {
              const inMonth = isInMonth(date, year, month);
              const isFocusable = inMonth && rowIndex === focusedRow && colIndex === focusedCol;

              return (
                <DayCell
                  key={colIndex}
                  ref={(el) => {
                    cellRefs.current[rowIndex][colIndex] = el;
                  }}
                  date={date}
                  displayedMonth={displayedMonth}
                  today={today}
                  data={cellDataGrid[rowIndex][colIndex]}
                  onDayClick={inMonth ? handleDayClick : undefined}
                  tabIndex={isFocusable ? 0 : -1}
                  onFocus={
                    inMonth
                      ? () => {
                          setFocusedRow(rowIndex);
                          setFocusedCol(colIndex);
                        }
                      : undefined
                  }
                />
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CalendarGrid;
